import { useCallback, useEffect, useRef } from "react";

const findVerticalScrollContainer = (element) => {
  let current = element?.parentElement;

  while (current && current !== document.body) {
    const styles = window.getComputedStyle(current);
    const hasScrollableOverflow = /(auto|scroll)/.test(styles.overflowY);

    if (hasScrollableOverflow && current.scrollHeight > current.clientHeight + 1) {
      return current;
    }

    current = current.parentElement;
  }

  return document.scrollingElement;
};

export default function useDragAutoScroll(isDragging, isEnabled = true) {
  const frameRef = useRef(null);
  const scrollStateRef = useRef({
    container: null,
    direction: 0,
    speed: 0,
  });

  const stopDragAutoScroll = useCallback(() => {
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    scrollStateRef.current = {
      container: null,
      direction: 0,
      speed: 0,
    };
  }, []);

  useEffect(() => stopDragAutoScroll, [stopDragAutoScroll]);

  const runAutoScroll = useCallback(function scrollFrame() {
    const { container, direction, speed } = scrollStateRef.current;

    if (!container || direction === 0 || speed === 0) {
      frameRef.current = null;
      return;
    }

    const scrollAmount = direction * speed;
    if (container === document.scrollingElement) {
      window.scrollBy(0, scrollAmount);
    } else {
      container.scrollTop += scrollAmount;
    }

    frameRef.current = window.requestAnimationFrame(scrollFrame);
  }, []);

  const handleDragAutoScroll = useCallback(
    (event) => {
      if (!isDragging || !isEnabled) {
        stopDragAutoScroll();
        return;
      }

      event.preventDefault();
      const container = findVerticalScrollContainer(event.target);
      const bounds =
        container === document.scrollingElement
          ? { top: 0, bottom: window.innerHeight }
          : container.getBoundingClientRect();
      const visibleHeight = Math.max(1, bounds.bottom - bounds.top);
      const edgeSize = Math.min(120, Math.max(72, visibleHeight * 0.18));
      const pointerY = event.clientY;
      let direction = 0;
      let proximity = 0;

      if (pointerY < bounds.top + edgeSize) {
        direction = -1;
        proximity = Math.min(1, (bounds.top + edgeSize - pointerY) / edgeSize);
      } else if (pointerY > bounds.bottom - edgeSize) {
        direction = 1;
        proximity = Math.min(
          1,
          (pointerY - (bounds.bottom - edgeSize)) / edgeSize,
        );
      }

      scrollStateRef.current = {
        container,
        direction,
        speed: direction === 0 ? 0 : 4 + proximity * 18,
      };

      if (direction === 0) {
        if (frameRef.current !== null) {
          window.cancelAnimationFrame(frameRef.current);
          frameRef.current = null;
        }
        return;
      }

      if (frameRef.current === null) {
        frameRef.current = window.requestAnimationFrame(runAutoScroll);
      }
    },
    [isDragging, isEnabled, runAutoScroll, stopDragAutoScroll],
  );

  return { handleDragAutoScroll, stopDragAutoScroll };
}
